import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}

(async () => {
  await customElements.whenDefined('shopify-account');
  const account = document.querySelector('shopify-account');
  if (!account?.shadowRoot) return;

  const outerSheet = new CSSStyleSheet();
  outerSheet.replaceSync(`
    .account__header-content { order: 2; }
    @media (min-width: 750px) {
      .dialog { inset-inline-start: var(--app-page-spacing); inset-inline-end: auto; }
    }
  `);
  account.shadowRoot.adoptedStyleSheets = [
    ...account.shadowRoot.adoptedStyleSheets,
    outerSheet
  ];

  // Function to inject into the inner shadow root
  async function injectLoginFormStyles() {
    await customElements.whenDefined('shopify-login-form');
    const loginForm = account.shadowRoot.querySelector('shopify-login-form');
    if (!loginForm?.shadowRoot) return false;

    // Check if we already injected
    if (loginForm.shadowRoot._rtlInjected) return true;

    const innerSheet = new CSSStyleSheet();
    innerSheet.replaceSync(`
      .account__email-submit { inset-inline-start: var(--submit-button-gutter); inset-inline-end: 0; }
      .account__email-input { text-align: right; padding-inline: 56px var(--padding-base); }
      .account__email-label { inset-inline-end: var(--padding-base); inset-inline-start: 0; text-align: right; }
    `);
    loginForm.shadowRoot.adoptedStyleSheets = [
      ...loginForm.shadowRoot.adoptedStyleSheets,
      innerSheet
    ];
    loginForm.shadowRoot._rtlInjected = true;
    return true;
  }

  // Try immediately
  if (!await injectLoginFormStyles()) {
    // If not ready, watch for it
    const observer = new MutationObserver(async () => {
      if (await injectLoginFormStyles()) {
        observer.disconnect();
      }
    });
    observer.observe(account.shadowRoot, { childList: true, subtree: true });
  }
})();